import React, { useState, useEffect } from 'react';

interface PriceRangeSliderProps {
  minValue: number;
  maxValue: number;
  step?: number;
  onChange: (minPrice: number, maxPrice: number) => void;
  defaultMinPrice?: number;
  defaultMaxPrice?: number;
}

const PriceRangeSlider: React.FC<PriceRangeSliderProps> = ({
  minValue,
  maxValue,
  step = 10,
  onChange,
  defaultMinPrice,
  defaultMaxPrice
}) => {
  const [minPrice, setMinPrice] = useState<number>(defaultMinPrice || minValue);
  const [maxPrice, setMaxPrice] = useState<number>(defaultMaxPrice || maxValue);
  const [isInitialized, setIsInitialized] = useState(false);
  
  // Initialize the component with default values
  useEffect(() => {
    if (!isInitialized && defaultMinPrice !== undefined && defaultMaxPrice !== undefined) {
      setMinPrice(defaultMinPrice);
      setMaxPrice(defaultMaxPrice);
      setIsInitialized(true);
    }
  }, [defaultMinPrice, defaultMaxPrice, isInitialized]);
  
  // Handle min price change
  const handleMinPriceChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newMinPrice = Math.min(Number(e.target.value), maxPrice - step);
    setMinPrice(newMinPrice);
    onChange(newMinPrice, maxPrice);
  };
  
  // Handle max price change
  const handleMaxPriceChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newMaxPrice = Math.max(Number(e.target.value), minPrice + step);
    setMaxPrice(newMaxPrice);
    onChange(minPrice, newMaxPrice);
  };
  
  // Calculate percentage for slider tracks
  const minPercentage = ((minPrice - minValue) / (maxValue - minValue)) * 100;
  const maxPercentage = ((maxPrice - minValue) / (maxValue - minValue)) * 100;
  
  return (
    <div className="price-range-slider">
      {/* Slider inputs */}
      <div className="relative h-2 mt-8 mb-6">
        {/* Slider track */}
        <div className="absolute inset-0 bg-background-light rounded-md"></div>
        
        {/* Active range */}
        <div 
          className="absolute h-full bg-accent/70 rounded-md"
          style={{ left: `${minPercentage}%`, right: `${100 - maxPercentage}%` }}
        ></div>
        
        {/* Min thumb */}
        <input
          type="range"
          min={minValue}
          max={maxValue}
          step={step}
          value={minPrice}
          onChange={handleMinPriceChange}
          className="absolute w-full h-full appearance-none bg-transparent pointer-events-auto cursor-pointer z-10"
          style={{ 
            background: 'transparent',
            // Custom styling for the thumb
            WebkitAppearance: 'none',
            appearance: 'none'
          }}
        />
        
        {/* Max thumb */}
        <input
          type="range"
          min={minValue}
          max={maxValue}
          step={step}
          value={maxPrice}
          onChange={handleMaxPriceChange}
          className="absolute w-full h-full appearance-none bg-transparent pointer-events-auto cursor-pointer z-10"
          style={{ 
            background: 'transparent',
            // Custom styling for the thumb
            WebkitAppearance: 'none',
            appearance: 'none'
          }}
        />
      </div>
      
      {/* Price display */}
      <div className="flex justify-between">
        <div className="text-sm">
          <span className="text-secondary">Min:</span> ${minPrice}
        </div>
        <div className="text-sm">
          <span className="text-secondary">Max:</span> ${maxPrice}
        </div>
      </div>
      
      {/* Optional input fields for direct value entry */}
      <div className="flex gap-2 mt-3">
        <div className="w-1/2">
          <input
            type="number"
            min={minValue}
            max={maxPrice - step}
            value={minPrice}
            onChange={(e) => {
              const value = Number(e.target.value);
              if (value >= minValue && value <= maxPrice - step) {
                setMinPrice(value);
                onChange(value, maxPrice);
              }
            }}
            className="w-full bg-background-light border border-gray-700 rounded-md py-1 px-2 text-primary text-sm focus:border-accent focus:ring-1 focus:ring-accent"
          />
        </div>
        <div className="w-1/2">
          <input
            type="number"
            min={minPrice + step}
            max={maxValue}
            value={maxPrice}
            onChange={(e) => {
              const value = Number(e.target.value);
              if (value <= maxValue && value >= minPrice + step) {
                setMaxPrice(value);
                onChange(minPrice, value);
              }
            }}
            className="w-full bg-background-light border border-gray-700 rounded-md py-1 px-2 text-primary text-sm focus:border-accent focus:ring-1 focus:ring-accent"
          />
        </div>
      </div>
    </div>
  );
};

export default PriceRangeSlider; 